// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { InlineLoading } from '@carbon/react';
import LivePlanHero from './LivePlanHero';
import LivePlanSummary from './LivePlanSummary';
import LiveUnplannedHero, { unplannedReason } from './LiveUnplannedHero';
import LiveUnplannedSummary from './LiveUnplannedSummary';
import LiveChartGrid from './LiveChartGrid';
import { LiveRevokeNote } from './LiveRevokeButton';
import { EmptyState } from '../../../components/shell/ScreenHelpers';
import { usePlanNarration } from '../../../hooks/usePlanNarration';
import { useLogicalNodes } from '../../../hooks/useLogicalNodes';
import { useLivePlanTelemetry } from '../../../hooks/useLivePlanTelemetry';
import { useHvacLoad } from '../../../hooks/useHvacLoad';
import { useChillerPlant } from '../../../hooks/useChillerPlant';
import { useAmbientObservations } from '../../../hooks/useAmbientObservations';
import { getStringAttr } from '../../../utils/planAttributes';
import { resolveOrgId } from '../../../utils/logicalNodeResolution';
import { pollWindowInstant } from '../../../utils/pollWindow';
import { useSiteTime } from '../../../hooks/useSiteTime';
import {
  BASELINE_STRATEGY,
  axisMinuteOfDay,
  findSolution,
  parseTimeOfDay,
  siteDayAxis,
} from '../../../utils/planSeries';
import { zonedDayKey } from '../../../utils/siteTime';
import type { HitlApproval, PlanFile, PlanPhase } from '../../../types';
import './LivePlan.css';

/** The phase whose `HH:MM` window contains `now`, on the site's clock. */
function activePhaseAt(
  planFile: PlanFile | null,
  now: Date,
  timeZone?: string
): PlanPhase | undefined {
  const startTime = planFile?.raw_inputs?.time_axis?.start_time;
  if (!planFile?.phases || !startTime) return undefined;
  const minuteOfDay = axisMinuteOfDay(now, startTime, timeZone);

  return planFile.phases.find(phase => {
    const start = parseTimeOfDay(phase.time_window?.start ?? '');
    const end = parseTimeOfDay(phase.time_window?.end ?? '');
    if (start === undefined || end === undefined) return false;
    return end > start
      ? minuteOfDay >= start && minuteOfDay < end
      : minuteOfDay >= start || minuteOfDay < end;
  });
}

export interface LivePlanPanelProps {
  /**
   * The executing plan; with `planned` false, today's plan that isn't running
   * (rejected, unapproved, or outside its window), or none at all.
   */
  approval?: HitlApproval;
  /** False when no plan is executing: the panel shows the baseline and live data. */
  planned?: boolean;
  /** Whether a plan is awaiting review, for the no-plan panel's link. */
  hasPendingReview?: boolean;
  siteId: string;
  now: Date;
}

/**
 * The Live tab's content: one executing plan, or — when none is — the site's
 * live data against today's baseline.
 *
 * With no plan running, the baseline comes from today's narration even though
 * the plan was never approved: the optimizer writes the narration when it runs,
 * so a rejected or unreviewed plan still carries its baseline solve. With no
 * narration for today at all, the measured curves draw alone on the site's day.
 *
 * Four reads compose it: the narration (the plan's forecast curves and prose),
 * the site's logical nodes (the only publisher of the ids telemetry needs), the
 * measured series for those ids, and the two composed Live endpoints — HVAC
 * electrical load and the chiller plant. The charts render as soon as the
 * narration lands; the live curves fill in behind.
 */
export default function LivePlanPanel({
  approval,
  planned = true,
  hasPendingReview = false,
  siteId,
  now,
}: LivePlanPanelProps) {
  const storeKey = getStringAttr(approval?.attributes, 'objectKey');
  const {
    planFile,
    loading: narrationLoading,
    error: narrationError,
  } = usePlanNarration(approval?.siteId, storeKey);
  const { siteZone, timeZone } = useSiteTime();

  // Only the calendar day moves the synthesized axis; keying on `now` itself
  // would hand every poll hook a new axis, and a new query key, each tick.
  const dayKey = zonedDayKey(now, timeZone);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const siteDay = useMemo(() => siteDayAxis(now, timeZone), [dayKey, timeZone]);
  const planAxis = planFile?.raw_inputs?.time_axis;
  const timeAxis = planned ? planAxis : narrationLoading ? undefined : (planAxis ?? siteDay);
  const hasBaseline = !planned && Boolean(findSolution(planFile?.raw_inputs, BASELINE_STRATEGY));
  const { nodes, error: nodesError } = useLogicalNodes(siteId);
  const orgId = useMemo(() => resolveOrgId(nodes), [nodes]);

  const { grid: gridRead, itLoad: itLoadRead } = useLivePlanTelemetry(
    siteId,
    hasBaseline || planned ? planFile?.raw_inputs : undefined,
    nodes,
    timeAxis
  );
  // Without the site's logical nodes there is nothing to ask telemetry for. Say
  // the membership read failed rather than that no node is published.
  const nodesFailed = Boolean(nodesError) && nodes.length === 0;
  const grid = useMemo(
    () => (nodesFailed ? { ...gridRead, requestFailed: true } : gridRead),
    [gridRead, nodesFailed]
  );
  const itLoad = useMemo(
    () => (nodesFailed ? { ...itLoadRead, requestFailed: true } : itLoadRead),
    [itLoadRead, nodesFailed]
  );
  const ambient = useAmbientObservations(timeAxis, siteId);
  const hvacLoadRead = useHvacLoad(siteId, orgId, timeAxis);
  // The HVAC read needs the org, which only the logical nodes carry.
  const hvacLoad = useMemo(
    () =>
      nodesFailed && !hvacLoadRead.data
        ? { ...hvacLoadRead, coverage: { ...hvacLoadRead.coverage, requestFailed: true } }
        : hvacLoadRead,
    [hvacLoadRead, nodesFailed]
  );
  const chillerPlant = useChillerPlant(siteId, timeAxis);

  const boundary = pollWindowInstant(now).getTime();
  const chartNow = useMemo(() => new Date(boundary), [boundary]);

  const activePhase = useMemo(
    () => (planned ? activePhaseAt(planFile, chartNow, siteZone) : undefined),
    [planned, planFile, chartNow, siteZone]
  );

  const liveData = {
    itLoad,
    grid,
    ambientObserved: ambient.values,
    ambientFailed: ambient.failed,
    hvacLoad,
    chillerPlant,
    now: chartNow,
  };

  if (!planned) {
    return (
      <div className="live-panel">
        <div className="live-panel-top">
          <LiveUnplannedHero
            reason={unplannedReason(approval, now)}
            approval={approval}
            hasBaseline={hasBaseline || narrationLoading}
            now={now}
          />
          <LiveUnplannedSummary
            approval={approval}
            hasBaseline={hasBaseline || narrationLoading}
            hasPendingReview={hasPendingReview}
          />
        </div>

        {!timeAxis ? (
          <InlineLoading
            className="hs-bg-loading"
            description="Loading today's baseline…"
            status="active"
          />
        ) : (
          <LiveChartGrid
            siteId={siteId}
            mode={hasBaseline ? 'baseline' : 'measured'}
            planFile={hasBaseline ? planFile : null}
            timeAxis={timeAxis}
            {...liveData}
          />
        )}
      </div>
    );
  }

  if (!approval) return null;

  return (
    <div className="live-panel">
      <LiveRevokeNote />

      <div className="live-panel-top">
        <LivePlanHero approval={approval} activePhase={activePhase} />
        <LivePlanSummary approval={approval} narrative={planFile?.drivers?.narrative} />
      </div>

      {narrationLoading ? (
        <InlineLoading
          className="hs-bg-loading"
          description="Loading this plan's telemetry…"
          status="active"
        />
      ) : narrationError ? (
        <EmptyState
          big="Couldn't load this plan's curves."
          sub={`${narrationError} The plan is still executing — this affects the charts only.`}
        />
      ) : !planFile?.raw_inputs ? (
        <EmptyState
          big="This plan carries no optimizer output."
          sub="Charts need the per-timestep solve in the narration's raw_inputs, which this plan predates."
        />
      ) : (
        <LiveChartGrid
          siteId={siteId}
          planFile={planFile}
          timeAxis={planFile.raw_inputs.time_axis}
          {...liveData}
        />
      )}
    </div>
  );
}
